import React from 'react';
import { Container, Typography, Box } from '@mui/material';
import { useTranslation } from '../hooks/useTranslation';
import { useCookieConsent } from '../contexts/CookieConsentContext';

const CookiePolicy: React.FC = () => {
  const { t } = useTranslation();
  const { openSettings } = useCookieConsent();

  return (
    <div className="min-h-screen bg-white">
      <Container maxWidth="lg" className="py-8 px-8">
        <Typography 
          variant="h4" 
          component="h1" 
          className="mb-6 text-left text-black"
        >
          Cookie Policy
        </Typography>
        
        <Typography variant="body2" className="mb-8 text-left text-black">
          Last updated: {new Date().toLocaleDateString()}
        </Typography>

        <Box className="mb-6">
          <Typography variant="body2" className="text-black">
            You can manage your cookie preferences at any time by clicking{' '}
            <button 
              onClick={openSettings}
              className="underline hover:no-underline cursor-pointer text-black bg-transparent border-none p-0 font-inherit"
            >
              here
            </button>
            .
          </Typography>
        </Box>

        <Box className="space-y-6">
          <section>
            <Typography variant="h6" component="h2" className="mb-4 text-black">
              1. What Are Cookies?
            </Typography>
            <Typography variant="body1" className="mb-4 text-black leading-relaxed">
              Cookies are small text files that are placed on your computer or mobile device when you visit 
              our website. They are widely used to make websites work more efficiently and to provide 
              information to the owners of the site.
            </Typography>
            <Typography variant="body1" className="text-black leading-relaxed">
              Cookies help us understand how visitors interact with our website by collecting and reporting 
              information anonymously. They also help us provide you with a better, more personalized 
              experience when you browse our website.
            </Typography>
          </section>



          <section>
            <Typography variant="h6" component="h2" className="mb-4 text-black">
              2. Types of Cookies We Use
            </Typography>
            
            <Box className="space-y-6">
              <Box className=" bg-white ">
                <Typography variant="h6" component="h3" className="mb-3 text-black">
                  2.1 Strictly Necessary Cookies
                </Typography>
                <Typography variant="body1" className="mb-3 text-black leading-relaxed">
                  These cookies are essential for you to browse the website and use its features. Without 
                  these cookies, services you have asked for cannot be provided.
                </Typography>
                <Typography variant="body1" className="text-black mb-2"><strong>Purpose:</strong></Typography>
                <ul className="list-disc pl-6 mb-3 text-black space-y-1">
                  <li>User authentication and login sessions</li>
                  <li>Shopping cart functionality</li>
                  <li>Security and fraud prevention</li>
                  <li>Remember your cookie consent preferences</li>
                </ul>
                <Typography variant="body1" className="text-black mb-2"><strong>Legal Basis:</strong></Typography>
                <Typography variant="body1" className="text-black">
                  Legitimate interest (essential website functionality)
                </Typography>
                <Typography variant="body1" className="text-black mb-2 mt-2"><strong>Can be disabled:</strong></Typography>
                <Typography variant="body1" className="text-black">No - Required for website functionality</Typography>
              </Box>

              <Box className=" bg-white ">
                <Typography variant="h6" component="h3" className="mb-3 text-black">
                  2.2 Analytics Cookies
                </Typography>
                <Typography variant="body1" className="mb-3 text-black leading-relaxed">
                  These cookies collect information about how visitors use our website. The information 
                  is collected anonymously and is used to improve how our website works.
                </Typography>
                <Typography variant="body1" className="text-black mb-2"><strong>Purpose:</strong></Typography>
                <ul className="list-disc pl-6 mb-3 text-black space-y-1">
                  <li>Understand visitor behavior and popular content</li>
                  <li>Identify technical issues and improve website performance</li>
                  <li>Measure the effectiveness of our content</li>
                  <li>Generate statistical reports on website usage</li>
                </ul>
                <Typography variant="body1" className="text-black mb-2"><strong>Third-party services:</strong></Typography>
                <ul className="list-disc pl-6 mb-3 text-black space-y-1">
                  <li>Google Analytics (anonymized IP addresses)</li>
                </ul>
                <Typography variant="body1" className="text-black mb-2"><strong>Legal Basis:</strong></Typography>
                <Typography variant="body1" className="text-black">Your explicit consent</Typography>
                <Typography variant="body1" className="text-black mb-2 mt-2"><strong>Can be disabled:</strong></Typography>
                <Typography variant="body1" className="text-black">Yes - Optional with your consent</Typography>
              </Box>

              <Box className=" bg-white ">
                <Typography variant="h6" component="h3" className="mb-3 text-black">
                  2.3 Marketing Cookies
                </Typography>
                <Typography variant="body1" className="mb-3 text-black leading-relaxed">
                  These cookies are used to deliver advertisements more relevant to you and your interests. 
                  They may also be used to limit the number of times you see an advertisement.
                </Typography>
                <Typography variant="body1" className="text-black mb-2"><strong>Purpose:</strong></Typography>
                <ul className="list-disc pl-6 mb-3 text-black space-y-1">
                  <li>Show relevant advertisements on other websites</li>
                  <li>Measure the effectiveness of advertising campaigns</li>
                  <li>Remember your preferences for personalized content</li>
                  <li>Prevent showing the same ad repeatedly</li>
                </ul>
                <Typography variant="body1" className="text-black mb-2"><strong>Legal Basis:</strong></Typography>
                <Typography variant="body1" className="text-black">Your explicit consent</Typography>
                <Typography variant="body1" className="text-black mb-2 mt-2"><strong>Can be disabled:</strong></Typography>
                <Typography variant="body1" className="text-black">Yes - Optional with your consent</Typography>
              </Box>

              <Box className=" bg-white ">
                <Typography variant="h6" component="h3" className="mb-3 text-black">
                  2.4 Functional Cookies
                </Typography>
                <Typography variant="body1" className="mb-3 text-black leading-relaxed">
                  These cookies allow the website to remember choices you make and provide enhanced features 
                  and personal content.
                </Typography>
                <Typography variant="body1" className="text-black mb-2"><strong>Purpose:</strong></Typography>
                <ul className="list-disc pl-6 mb-3 text-black space-y-1">
                  <li>Remember your language preference</li>
                  <li>Save your theme and display preferences</li>
                  <li>Remember items in your wishlist</li>
                  <li>Provide enhanced features like live chat</li>
                </ul>
                <Typography variant="body1" className="text-black mb-2"><strong>Legal Basis:</strong></Typography>
                <Typography variant="body1" className="text-black">Your explicit consent</Typography>
                <Typography variant="body1" className="text-black mb-2 mt-2"><strong>Can be disabled:</strong></Typography>
                <Typography variant="body1" className="text-black">Yes - Optional with your consent</Typography>
              </Box>
            </Box>
          </section>



          <section>
            <Typography variant="h6" component="h2" className="mb-4 text-black">
              3. Detailed Cookie Information
            </Typography>
            
            <Box className="overflow-x-auto">
              <table className="w-full text-sm text-black border border-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2  border border-gray-300 text-left">Cookie Name</th>
                    <th className="px-4 py-2  border border-gray-300 text-left">Purpose</th>
                    <th className="px-4 py-2  border border-gray-300 text-left">Duration</th>
                    <th className="px-4 py-2  border border-gray-300 text-left">Type</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="px-4 py-2  border border-gray-300">cookie_consent</td>
                    <td className="px-4 py-2  border border-gray-300">Stores your cookie consent preferences</td>
                    <td className="px-4 py-2  border border-gray-300">1 year</td>
                    <td className="px-4 py-2  border border-gray-300">Necessary</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-4 py-2  border border-gray-300">auth_token</td>
                    <td className="px-4 py-2  border border-gray-300">Keeps you logged in</td>
                    <td className="px-4 py-2  border border-gray-300">Session/30 days</td>
                    <td className="px-4 py-2  border border-gray-300">Necessary</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2  border border-gray-300">language_preference</td>
                    <td className="px-4 py-2  border border-gray-300">Remembers your language choice</td>
                    <td className="px-4 py-2  border border-gray-300">1 year</td>
                    <td className="px-4 py-2  border border-gray-300">Functional</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-4 py-2  border border-gray-300">_ga</td>
                    <td className="px-4 py-2  border border-gray-300">Google Analytics - distinguishes users</td>
                    <td className="px-4 py-2  border border-gray-300">2 years</td>
                    <td className="px-4 py-2  border border-gray-300">Analytics</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2  border border-gray-300">_ga_*</td>
                    <td className="px-4 py-2  border border-gray-300">Google Analytics - session information</td>
                    <td className="px-4 py-2  border border-gray-300">2 years</td>
                    <td className="px-4 py-2  border border-gray-300">Analytics</td>
                  </tr>
                </tbody>
              </table>
            </Box>
          </section>



          <section>
            <Typography variant="h6" component="h2" className="mb-4 text-black">
              4. How to Control Cookies
            </Typography>
            
            <Typography variant="h6" component="h3" className="mb-3 text-black">
              4.1 Our Cookie Settings
            </Typography>
            <Typography variant="body1" className="mb-4 text-black leading-relaxed">
              You can manage your cookie preferences using our cookie banner that appears on your first 
              visit, or by clicking the "Manage Cookie Settings" button at the top of this page. You can:
            </Typography>
            <ul className="list-disc pl-6 mb-4 text-black space-y-2">
              <li>Accept all cookies</li>
              <li>Accept only necessary cookies</li>
              <li>Customize your preferences for each category</li>
              <li>Change your preferences at any time</li>
            </ul>

            <Typography variant="h6" component="h3" className="mb-3 text-black">
              4.2 Browser Settings
            </Typography>
            <Typography variant="body1" className="mb-4 text-black leading-relaxed">
              Most web browsers allow you to control cookies through their settings. However, please note 
              that blocking all cookies may impact your experience on our website.
            </Typography>
            <ul className="list-disc pl-6 mb-4 text-black space-y-2">
              <li><strong>Chrome:</strong> Settings → Privacy and security → Cookies and other site data</li>
              <li><strong>Firefox:</strong> Options → Privacy & Security → Cookies and Site Data</li>
              <li><strong>Safari:</strong> Preferences → Privacy → Manage Website Data</li>
              <li><strong>Edge:</strong> Settings → Site permissions → Cookies and site data</li>
            </ul>

            <Typography variant="h6" component="h3" className="mb-3 text-black">
              4.3 Third-Party Tools
            </Typography>
            <ul className="list-disc pl-6 mb-4 text-black space-y-2">
              <li><strong>Google Analytics Opt-out:</strong> <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-black underline">Browser Add-on</a></li>
              <li><strong>Your Online Choices:</strong> <a href="http://www.youronlinechoices.com/" target="_blank" rel="noopener noreferrer" className="text-black underline">Industry opt-out platform</a></li>
            </ul>
          </section>



          <section>
            <Typography variant="h6" component="h2" className="mb-4 text-black">
              5. GDPR and Your Rights
            </Typography>
            <Typography variant="body1" className="mb-4 text-black leading-relaxed">
              Under the General Data Protection Regulation (GDPR), you have specific rights regarding 
              cookies and personal data:
            </Typography>
            <ul className="list-disc pl-6 mb-4 text-black space-y-2">
              <li><strong>Right to be informed:</strong> This Cookie Policy provides clear information about our cookie usage</li>
              <li><strong>Right to consent:</strong> You can give or withdraw consent for non-essential cookies</li>
              <li><strong>Right of access:</strong> You can request information about what data we collect through cookies</li>
              <li><strong>Right to erasure:</strong> You can request deletion of data collected through cookies</li>
              <li><strong>Right to data portability:</strong> You can request your cookie-related data in a portable format</li>
            </ul>
          </section>



          <section>
            <Typography variant="h6" component="h2" className="mb-4 text-black">
              6. Third-Party Cookies
            </Typography>
            <Typography variant="body1" className="mb-4 text-black leading-relaxed">
              Some cookies are set by third-party services that appear on our pages. We do not control 
              these cookies and recommend reading their privacy policies:
            </Typography>
            <ul className="list-disc pl-6 mb-4 text-black space-y-2">
              <li><strong>Google Analytics:</strong> <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-black underline">Google Privacy Policy</a></li>
              <li><strong>Stripe:</strong> <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-black underline">Stripe Privacy Policy</a></li>
            </ul>
          </section>



          <section>
            <Typography variant="h6" component="h2" className="mb-4 text-black">
              7. Updates to This Cookie Policy
            </Typography>
            <Typography variant="body1" className="text-black leading-relaxed">
              We may update this Cookie Policy from time to time to reflect changes in technology, 
              legislation, or our privacy practices. We will post any changes on this page and update 
              the "Last updated" date. If we make significant changes, we may also notify you through 
              our cookie banner.
            </Typography>
          </section>



          <section>
            <Typography variant="h6" component="h2" className="mb-4 text-black">
              8. Contact Information
            </Typography>
            <Typography variant="body1" className="mb-4 text-black leading-relaxed">
              If you have any questions about this Cookie Policy or our use of cookies, please contact us:
            </Typography>
            <Box className="mt-4  bg-white rounded">
              <Typography variant="body1" className="text-black">
                <strong>SonicMakers Ltd.</strong><br />
                Email: privacy@sonicmakers.com<br />
                Website: https://sonicmakers.com
              </Typography>
            </Box>
          </section>
        </Box>
      </Container>
    </div>
  );
};

export default CookiePolicy;